import type { FixCheck } from '@testflow/shared';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { pickLang } from '../../lib/pick-lang.js';
import { matchesQuery } from '../../lib/row-filter.js';
import { CardFrame } from './card-frame.js';

// =================================================================================================
// #194: Karte „Fehler prüfen" — der Melder eines nicht blockierenden Fehlers (oder seine aktive
// Vertretung) bestätigt, dass die Behebung stimmt. Der Testfall wird dabei NICHT neu ausgeführt.
//
// Nachtrag 14.10.: Die Karte verhält sich wie eine Testkarte — ein Klick wählt sie aus, und rechts
// (wo sonst die Ausführung steht) öffnet sich das Detail mit Verlauf, Kommentarfeld und den Knöpfen
// „Behoben"/„Nicht behoben" (fix-check-detail.tsx). Die Karte selbst trägt keine Knöpfe mehr und kein
// „bleibt bestanden"; sie ist als Ganzes EIN Knopf mit `aria-pressed`. Deshalb auch kein Link auf
// die Defect-ID mehr: <a> im <button> ist ungültiges HTML, und der Weg zum Defect ist jetzt das
// Detail. Als einzige Karte orange gerahmt (15.10.: Testkarten inkl. Retest sind neutral); Kopfzeile, Rahmen und Auswahl-
// Ring kommen aus dem gemeinsamen Kartenrahmen (card-frame.tsx, N4).
// =================================================================================================

/**
 * #194: Suche der Playlist — eine Karte bleibt sichtbar, wenn Defect-ID, Defect-Titel, Testfall-ID
 * oder einer der Testfall-Titel passt. Eine Stelle für Status- und E2E-Ansicht.
 */
export function fixCheckMatches(query: string, check: FixCheck): boolean {
  return matchesQuery(
    query,
    check.human_id,
    check.title,
    check.test_case_human_id,
    check.test_case_title_de,
    check.test_case_title_en,
  );
}

export function FixCheckCard({
  check,
  selected,
  onSelect,
}: {
  check: FixCheck;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    // Ziel des Deeplinks `?fixcheck=` (Glocke `retest_due`) ist eine AUSWAHL — sie scrollt sich in
    // den Blick. Beim Klick ist die Karte schon sichtbar; `nearest` bewegt dann nichts.
    // jsdom kennt scrollIntoView nicht — daher optional aufgerufen.
    if (selected) ref.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [selected]);

  const testCaseTitle = pickLang(lang, check.test_case_title_de, check.test_case_title_en);

  return (
    // Nachtrag N4: gemeinsamer Kartenrahmen (card-frame.tsx) im Ton `retest` — orange Kopfzeile und
    // Rahmen. Links die Defect-ID wie auf der Testkarte die Testfall-ID, rechts das Label. Die
    // Auswahl ist der Ring des Rahmens (nicht Orange, das hier „Handlungsbedarf" heißt).
    <CardFrame
      ref={ref}
      as="button"
      tone="retest"
      selected={selected}
      header={{ left: check.human_id, right: t('fixCheck.label') }}
      data-testid="fix-check"
      aria-pressed={selected}
      onClick={onSelect}
    >
      <span className="flex w-full flex-col gap-1.5 px-3 py-2">
        <span className="break-words text-sm font-semibold text-text-primary">{check.title}</span>
        {/* test_case_id ist am Defect nullable — ohne Testfall gibt es keinen Bezug. */}
        {check.test_case_human_id && (
          <span className="min-w-0 break-words text-[11px] text-text-muted">
            {t('fixCheck.from', { id: check.test_case_human_id, title: testCaseTitle })}
          </span>
        )}
        {/* Vertretung (B4): der Melder ist abwesend, sein Vertreter prüft — die Karte sagt, für wen. */}
        {check.substituted_for && (
          <span className="self-start rounded-chip bg-[var(--tint-blue)] px-2 py-0.5 text-[11px] text-text-body">
            {t('exec.substitutedFor', { name: check.substituted_for.name })}
          </span>
        )}
      </span>
    </CardFrame>
  );
}

// #194: was Status- und E2E-Ansicht für die Karten „Fehler prüfen" brauchen. Die Seite
// (my-tests-page.tsx) hält Abfrage, Planfilter, Auswahl und Mutation; die Listen zeigen nur die
// Karten. Nachtrag 14.10.: Auswahl statt Aktion — „Behoben"/„Nicht behoben" stehen im Detail rechts.
export interface FixCheckListProps {
  /** Offene Fehlerprüfungen des GEWÄHLTEN Plans (die Seite filtert). */
  fixChecks: FixCheck[];
  /** Defect-ID der gewählten Karte (auch Ziel des Deeplinks `?fixcheck=`), sonst null. */
  selectedFixCheckId: string | null;
  onSelectFixCheck: (defectId: string) => void;
  /** Gesetzt, wenn die Abfrage der Fehlerprüfungen gescheitert ist (#89: nie als Leerzustand). */
  onRetryFixChecks?: (() => void) | undefined;
  /** Die Abfrage läuft noch — bei leerer Playlist Ladeanzeige statt Leerzustand. */
  fixChecksLoading?: boolean | undefined;
}

// #194: eine Kartenliste für beide Ansichten — Reihenfolge wie vom Server.
export function FixCheckCards({
  checks,
  selectedFixCheckId,
  onSelectFixCheck,
}: Pick<FixCheckListProps, 'selectedFixCheckId' | 'onSelectFixCheck'> & {
  checks: FixCheck[];
}) {
  return (
    <>
      {checks.map((check) => (
        <FixCheckCard
          key={check.defect_id}
          check={check}
          selected={selectedFixCheckId === check.defect_id}
          onSelect={() => onSelectFixCheck(check.defect_id)}
        />
      ))}
    </>
  );
}
